import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet, useOutletContext, useSearchParams } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./context/AuthContext";
import api from "./lib/api";
import { trackPageView } from "./lib/track";
import Footer from "./components/Footer";
import HelpButton from "./components/HelpButton";
import AuthCallback from "./components/AuthCallback";
import CookieConsent from "./components/CookieConsent";
import CompareBar from "./components/CompareBar";
import Analytics from "./components/Analytics";
import { LANDINGS } from "./lib/landings";
import { LanguageProvider } from "./i18n";
import { CompareProvider } from "./context/CompareContext";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import "./components/maquette/maquette.css";
import V2Header from "./components/maquette/Header";
import { PromoBar } from "./components/maquette/Season";
import { SEASONS } from "./components/maquette/seasons";

// Reload once on a stale-chunk error (after a new deploy) so navigation never
// lands on a blank page. Guarded by a timestamp to avoid infinite reload loops.
const lazyWithRetry = (factory) =>
  lazy(async () => {
    const KEY = "chunk_reload_ts";
    try {
      const mod = await factory();
      sessionStorage.removeItem(KEY);
      return mod;
    } catch (err) {
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return { default: () => null };
      }
      throw err;
    }
  });

// Route-level code splitting: everything below Home loads on demand (smaller initial bundle).
const Catalogue = lazyWithRetry(() => import("./pages/Catalogue"));
const MaquetteAccueil = lazyWithRetry(() => import("./pages/MaquetteAccueil"));
const CategoryPage = lazyWithRetry(() => import("./pages/CategoryPage"));
const Product = lazyWithRetry(() => import("./pages/Product"));
const Cart = lazyWithRetry(() => import("./pages/Cart"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const CheckoutReturn = lazyWithRetry(() => import("./pages/CheckoutReturn"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Account = lazyWithRetry(() => import("./pages/Account"));
const Neuf = lazyWithRetry(() => import("./pages/Neuf"));
const Preorder = lazyWithRetry(() => import("./pages/Preorder"));
const StaticPage = lazyWithRetry(() => import("./pages/StaticPage"));
const About = lazyWithRetry(() => import("./pages/About"));
const NosGrades = lazyWithRetry(() => import("./pages/NosGrades"));
const Stores = lazyWithRetry(() => import("./pages/Stores"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const Reprise = lazyWithRetry(() => import("./pages/Reprise"));
const CoquePerso = lazyWithRetry(() => import("./pages/CoquePerso"));
const RepriseOffre = lazyWithRetry(() => import("./pages/RepriseOffre"));
const RepriseSuivi = lazyWithRetry(() => import("./pages/RepriseSuivi"));
const SeasonPage = lazyWithRetry(() => import("./pages/SeasonPage"));
const Location = lazyWithRetry(() => import("./pages/Location"));
const RentalCheckout = lazyWithRetry(() => import("./pages/RentalCheckout"));
const Comparateur = lazyWithRetry(() => import("./pages/Comparateur"));
const Quiz = lazyWithRetry(() => import("./pages/Quiz"));
const GiftCard = lazyWithRetry(() => import("./pages/GiftCard"));
const Landing = lazyWithRetry(() => import("./pages/Landing"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const SupportTicket = lazyWithRetry(() => import("./pages/SupportTicket"));
const MaintenancePage = lazyWithRetry(() => import("./pages/MaintenancePage"));
const ComingSoon = lazyWithRetry(() => import("./pages/ComingSoon"));

function PageLoader() {
  return (
    <div className="grid min-h-[60vh] place-items-center" data-testid="page-loader">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-black/10 border-t-black" />
    </div>
  );
}

function Extras() {
  return <><CookieConsent /><CompareBar /><HelpButton /></>;
}

function V2Layout({ season }) {
  const [params] = useSearchParams();
  const key = params.has("saison") ? params.get("saison") : season?.key || "";
  const s = SEASONS[key];
  const promo = season?.key === key && season?.code ? season : null;
  const vars = s ? { "--blue": s.vars.blue, "--blue-h": s.vars.blue, "--link": s.vars.link, "--bg": s.vars.bg } : undefined;
  return (
    <div className="rda v2 flex min-h-screen flex-col" style={vars} data-season={key || "none"} data-testid="v2-layout">
      <PromoBar seasonKey={key} promo={promo} />
      <V2Header />
      <main className="flex-1 overflow-x-clip">
        <RouteErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Outlet context={{ v2: true, seasonKey: key, promo }} />
          </Suspense>
        </RouteErrorBoundary>
      </main>
      <Footer />
      <Extras />
    </div>
  );
}

function V2Shell({ children }) {
  const { data: pub } = useQuery({ queryKey: ["public-settings"], queryFn: async () => (await api.get("/settings/public")).data, staleTime: 30000 });
  const s = SEASONS[pub?.season?.key];
  return <div className="rda v2 min-h-screen" style={s ? { "--blue": s.vars.blue, "--blue-h": s.vars.blue, "--link": s.vars.link, "--bg": s.vars.bg } : undefined} data-testid="v2-shell">{children}</div>;
}

function HomeSwitch() {
  const ctx = useOutletContext();
  return <MaquetteAccueil embedded seasonKey={ctx?.seasonKey} promo={ctx?.promo} />;
}

function Layout() {
  const { data: pub } = useQuery({ queryKey: ["public-settings"], queryFn: async () => (await api.get("/settings/public")).data, staleTime: 30000 });
  return <V2Layout season={pub?.season} />;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { trackPageView(); }, [location.pathname]);
  const { user, loading: authLoading } = useAuth();
  const { data: pub } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await api.get("/settings/public")).data,
    staleTime: 30000,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const maint = pub?.maintenance;
  const staff = !!(user && user.role && user.role !== "customer");
  const path = location.pathname;
  const bypass = staff || path.startsWith("/admin") || path.startsWith("/connexion") || path.startsWith("/desinscription");
  // Google tag (GTM/GA4) must load on EVERY page — including the ComingSoon /
  // Maintenance gates — otherwise Google reports "tag not detected".
  const analytics = <Analytics gtm={pub?.seo_marketing?.gtm_id} ga4={pub?.seo_marketing?.ga4_id} gsc={pub?.seo_marketing?.gsc_verification} />;
  if (maint?.enabled && !authLoading && !bypass) return <>{analytics}<Suspense fallback={null}><MaintenancePage data={maint} /></Suspense></>;
  const pre = pub?.prelaunch;
  if (pre?.enabled && !authLoading && !bypass) return <>{analytics}<Suspense fallback={null}><ComingSoon data={pre} /></Suspense></>;
  return (
    <>
      {analytics}
      <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomeSwitch />} />
        <Route path="/catalogue" element={<Catalogue />} />
        <Route path="/neuf" element={<Neuf />} />
        <Route path="/categorie/:slug" element={<CategoryPage />} />
        <Route path="/produit/:slug" element={<Product />} />
        <Route path="/maquette-produit/:slug" element={<Product mockup />} />
        <Route path="/panier" element={<Cart />} />
        <Route path="/checkout/retour" element={<CheckoutReturn />} />
        <Route path="/connexion" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/compte" element={<Account />} />
        <Route path="/precommande/:id" element={<Preorder />} />
        <Route path="/nos-grades" element={<NosGrades />} />
        <Route path="/reprise" element={<Reprise />} />
        {process.env.REACT_APP_CASES_ENABLED === "1" && <Route path="/coque-personnalisee" element={<CoquePerso />} />}
        <Route path="/reprise/offre/:id" element={<RepriseOffre />} />
        <Route path="/reprise/suivi" element={<RepriseSuivi />} />
        <Route path="/offres/:key" element={<SeasonPage />} />
        <Route path="/location" element={<Location />} />
        <Route path="/location/checkout" element={<RentalCheckout />} />
        <Route path="/location/merci" element={<Location />} />
        <Route path="/comparateur" element={<Comparateur />} />
        <Route path="/trouver-mon-smartphone" element={<Quiz />} />
        <Route path="/cartes-cadeaux" element={<GiftCard />} />
        {Object.keys(LANDINGS).map((s) => (
          <Route key={s} path={`/${s}`} element={<Landing slug={s} />} />
        ))}
        <Route path="/boutiques" element={<Stores />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/a-propos" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/support/ticket/:id" element={<SupportTicket />} />
        <Route path="/faq" element={<StaticPage pageKey="faq" />} />
        <Route path="/cgv" element={<StaticPage pageKey="cgv" />} />
        <Route path="/conditions-generales-de-vente" element={<StaticPage pageKey="cgv" />} />
        <Route path="/conditions-location" element={<StaticPage pageKey="cgl" />} />
        <Route path="/conditions-reprise" element={<StaticPage pageKey="reprise" />} />
        <Route path="/conditions-retour" element={<StaticPage pageKey="retours" />} />
        <Route path="/politique-retour" element={<StaticPage pageKey="retours" />} />
        <Route path="/garantie" element={<StaticPage pageKey="garantie" />} />
        <Route path="/formulaire-retractation" element={<StaticPage pageKey="retractation" />} />
        <Route path="/paiement" element={<StaticPage pageKey="paiement" />} />
        <Route path="/accessibilite" element={<StaticPage pageKey="accessibilite" />} />
        <Route path="/mentions-legales" element={<StaticPage pageKey="mentions" />} />
        <Route path="/confidentialite" element={<StaticPage pageKey="confidentialite" />} />
        <Route path="/politique-de-confidentialite" element={<StaticPage pageKey="confidentialite" />} />
        <Route path="/cookies" element={<StaticPage pageKey="cookies" />} />
        <Route path="/politique-cookies" element={<StaticPage pageKey="cookies" />} />
        <Route path="/livraison" element={<StaticPage pageKey="livraison" />} />
        <Route path="/desinscription" element={<Unsubscribe />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="/checkout" element={<RouteErrorBoundary><Suspense fallback={<PageLoader />}><V2Shell><Checkout /></V2Shell></Suspense></RouteErrorBoundary>} />
      <Route path="/admin" element={<RouteErrorBoundary><Suspense fallback={<PageLoader />}><Admin /></Suspense></RouteErrorBoundary>} />
    </Routes>
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <LazyMotion features={domAnimation}>
      <LanguageProvider>
        <CompareProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRouter />
          </BrowserRouter>
        </CompareProvider>
      </LanguageProvider>
    </LazyMotion>
  );
}
